import React, { useCallback, useEffect, useRef } from "react";

import ShareIcon from "@uui/Share06";
import { DateTime } from "luxon";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import type { SxProps } from "@mui/material";

import HeaderButton from "@app/analytics/components/reports/HeaderButton";
import ReportTitle from "@app/analytics/components/reports/ReportTitle";
import {
  BaseHeaderContainer,
  HeaderDetailsContainer,
  VerticalDivider,
} from "@app/analytics/components/reports/styles";
import SignupCluster from "@app/analytics/MeetingReport/components/SignupCluster";
import DownloadMenu from "@app/analytics/MeetingReport/DownloadMenu";
import ReportActionsMenu from "@app/analytics/MeetingReport/ReportActionsMenu";
import useEditStore from "@app/analytics/MeetingReport/stores/edits";
import useMeetingReportUiStore from "@app/analytics/MeetingReport/stores/ui";
import * as Events from "@app/analytics/MeetingReport/tracking/events";
import { getTotalWidth } from "@app/analytics/utils";
import ShareReport from "@app/common/access/ShareReport";
import * as ShareEvents from "@app/common/access/ShareReport/events";
import * as ShareUtils from "@app/common/access/ShareReport/utils";
import useAuth from "@app/common/auth/useAuth";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import useMeasure from "@app/common/hooks/useMeasure";
import ROUTES from "@app/common/routes";
import type { EntityAccessLevel } from "@app/common/types";
import type { SSOProviderId } from "@app/common/types/providerTypes";

import Stack, { Row } from "@rui/Stack";
import { BackButton } from "@rui/styles";

import DateAndTime from "./DateAndTime";
import ExternalShareMenu from "./ExternalShareMenu";
import CopyLinkButton from "./ExternalShareMenu/CopyLinkButton";
import MeetingReportTags from "./MeetingReportTags";
import ParticipantsPopover from "./ParticipantsPopover";
import TierChip from "../components/TierChip";
import type { SessionMetadata } from "../types";

export interface MeetingReportHeaderProps {
  acl: EntityAccessLevel;
  onEditDateTime: (props: { startTime: DateTime }) => void;
  isInitialLoading: boolean;
  onEditTitle: (title: string) => void;
  startTime?: DateTime;
  endTime?: DateTime;
  onBackClick?: () => void;
  sessionMetadata: SessionMetadata;
  sx?: SxProps;
}

const MeetingReportHeader = ({
  acl,
  isInitialLoading,
  onEditTitle,
  startTime,
  endTime,
  onBackClick,
  sessionMetadata,
  onEditDateTime,
  sx,
}: MeetingReportHeaderProps) => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [queryParams] = useSearchParams();

  const { user, signUp } = useAuth();
  const { canAccessMeetingDownloads } = useFeatureAccess();

  const [containerRef, , { innerWidth }] = useMeasure();
  const actionsContentRef = useRef<HTMLDivElement | null>(null);

  const [dialog, setDialog] = useMeetingReportUiStore((state) => [
    state.dialog,
    state.setDialog,
  ]);
  const inEditMode = useEditStore((state) => state.editingSection != null);

  const titleMaxWidth =
    innerWidth -
    getTotalWidth(actionsContentRef.current) -
    12 -
    (onBackClick ? 32 : 0); // right padding and back button

  const title = sessionMetadata.title;
  const tier = sessionMetadata.tier;

  const canShareReport = ShareUtils.canShareReport(acl);
  const canEditReport = ShareUtils.canEdit(acl) && !sessionMetadata.dataDeleted;

  useEffect(() => {
    if (isInitialLoading || !canShareReport) return;
    if (
      queryParams.get("action") === "share" ||
      queryParams.get("shareEmail")
    ) {
      setDialog("share");
    } else if (queryParams.get("action") === "post_to_slack") {
      setDialog("post_to_slack");
    }
  }, [canShareReport, isInitialLoading]);

  const handleClickSignin = useCallback(() => {
    navigate(ROUTES.SIGN_IN, {
      state: { redirectTo: window.location.href },
    });
  }, []);

  const handleClickSignup = useCallback(
    (_e: React.MouseEvent, provider: SSOProviderId | "email") => {
      Events.signUpReportClick({ provider });

      if (provider === "email") {
        navigate(ROUTES.SIGN_UP, {
          state: { redirectTo: window.location.href },
        });
        return;
      }
      signUp({ action: provider, redirectTo: window.location.href });
    },
    []
  );

  const handleCloseShareReportDialog = () => {
    setDialog(null);
    // Clear the query params, so we don't try and
    // share again if the user opens the share modal again
    if (queryParams.get("shareEmail")) {
      navigate(location.pathname);
    }
  };

  const showTags = !sessionMetadata.isProcessing;
  const showParticipants = !(
    sessionMetadata.isNotetakerReport ||
    sessionMetadata.isProcessing ||
    sessionMetadata.dataDeleted ||
    ShareUtils.hasRecapOnlyAccess(acl)
  );
  const showDownloadMenu =
    canAccessMeetingDownloads && !sessionMetadata.isProcessing;

  return (
    <>
      <BaseHeaderContainer sx={{ height: "72px", ...sx }} ref={containerRef}>
        <Row spacing={1.5} variant="left" sx={{ height: "100%" }}>
          {onBackClick && <BackButton onClick={onBackClick} />}
          {title && (
            <Stack spacing={0.5}>
              <ReportTitle
                id="meeting-report-title"
                canEdit={canEditReport}
                value={title}
                onEditChange={onEditTitle}
                maxWidth={titleMaxWidth}
              />
              <HeaderDetailsContainer sx={{ maxWidth: `${titleMaxWidth}px` }}>
                {startTime && (
                  <DateAndTime
                    startTime={startTime}
                    endTime={endTime}
                    onEdit={onEditDateTime}
                    canEditReport={
                      canEditReport && sessionMetadata.isUploadedSession
                    }
                  />
                )}
                {tier !== "unknown" && (
                  <>
                    <VerticalDivider size="sm" />
                    <TierChip
                      tier={tier}
                      tooltip={sessionMetadata.tierTooltip}
                    />
                  </>
                )}
              </HeaderDetailsContainer>
            </Stack>
          )}
        </Row>
        <Row variant="right" spacing="6px" flexGrow={0} ref={actionsContentRef}>
          {showTags && <MeetingReportTags disabled={inEditMode} />}
          {showParticipants && <ParticipantsPopover disabled={inEditMode} />}
          {(showTags || showParticipants) && <VerticalDivider size="md" />}
          {showDownloadMenu && (
            <DownloadMenu title={sessionMetadata.title} disabled={inEditMode} />
          )}
          {user ? (
            <>
              <ExternalShareMenu
                canShareReport={canShareReport}
                inEditMode={inEditMode}
                isProcessing={sessionMetadata.isProcessing}
                sessionId={sessionId!}
              />
              <HeaderButton
                primary
                endIcon={<ShareIcon />}
                disabled={inEditMode}
                onClick={() => {
                  ShareEvents.shareReportClick();
                  setDialog("share");
                }}
              >
                Share
              </HeaderButton>
            </>
          ) : (
            <>
              <CopyLinkButton sessionId={sessionId!} disabled={inEditMode} />
              <SignupCluster
                onClickSignup={handleClickSignup}
                onClickSignin={handleClickSignin}
              />
            </>
          )}
          {Boolean(user) && (
            <ReportActionsMenu
              isMeetingOwner={sessionMetadata.currentUserIsMeetingOwner}
            />
          )}
        </Row>
      </BaseHeaderContainer>

      {/* Dialogs */}
      <ShareReport
        open={dialog === "share"}
        needsEditAccess={!canShareReport}
        initEntityEmail={queryParams.get("shareEmail")}
        initAccessLevel={
          queryParams.get("shareAccessLevel") as EntityAccessLevel | null
        }
        onClose={handleCloseShareReportDialog}
      />
    </>
  );
};

export default MeetingReportHeader;
